import {
  Order,
  OrderItemStatus,
  OrderSource,
  OrderStatus,
  OrderType,
} from "@/redux/services/order/order.type";
import type {
  PaymentMethodName,
  SessionStatus,
} from "@/redux/services/pos/pos.type";
import type { StockAdjustmentReason } from "@/redux/services/inventory/inventory.type";
import { Roles } from "@/redux/services/user/user.type";
import { type ClassValue, clsx } from "clsx";
import {
  differenceInCalendarDays,
  format,
  isToday,
  isYesterday,
  parseISO,
} from "date-fns";
import { fr } from "date-fns/locale";
import { twMerge } from "tailwind-merge";
import errors from "./errors.json";
import { QueryError } from "./type";
import { MenuItem, MenuItemOption } from "@/redux/services/menu/menu.type";
import { Notification as NotificationType } from "@/redux/services/notification/notification.type";
import { AllowedSideType } from "@/redux/services/menu/menu_combo.type";
import { parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function extractTimeFromDateTime(datetime: string): string {
  // Handle both HH:MM format and full datetime format (1970-01-01T11:00:00+00:00)
  if (datetime.includes("T")) {
    // Extract time from datetime string
    const timePart = datetime.split("T")[1]?.substring(0, 5);
    return timePart || "";
  }
  // Already in HH:MM format
  return datetime.substring(0, 5);
}

export function formatTimeDisplay(time?: string): string {
  if (!time) return "Non défini";
  // Handle both HH:MM format and full datetime format (1970-01-01T11:00:00+00:00)
  const extractedTime = extractTimeFromDateTime(time);
  if (extractedTime.includes(":")) {
    const [hours, minutes] = extractedTime.split(":");
    return `${hours}h${minutes}`;
  }
  return time;
}

export function cleanerError(
  errors: any,
  cleaner: any,
  timeOut: number = 5000
) {
  if (errors) {
    setTimeout(
      () => Object.entries(errors).map(([key]: any) => cleaner(key)),
      timeOut
    );
  }
}

export function getProfilePictureUrl(profileUrl?: string) {
  if (profileUrl && profileUrl.trim() !== "") {
    return profileUrl;
  }
}

export function getLogoUrl(logoUrl?: string) {
  if (logoUrl && logoUrl.trim() !== "") {
    return logoUrl;
  } else {
    return "/images/restaurant.png";
  }
}

export function getImageUrl(logoUrl?: string) {
  if (logoUrl && logoUrl.trim() !== "") {
    return logoUrl;
  } else {
    return "/images/icon.png";
  }
}

export function formatRoles(roles: Roles[]) {
  if (roles?.includes(Roles.admin)) {
    return "Admin";
  }
  if (roles?.includes(Roles.owner)) {
    return "Propriétaire";
  }
  if (roles?.includes(Roles.manager)) {
    return "Manager";
  }
  if (roles?.includes(Roles.waiter)) {
    return "Serveur";
  }
  if (roles?.includes(Roles.kitchen)) {
    return "Cuisine";
  }
  if (roles?.includes(Roles.support)) {
    return "Support";
  }

  if (roles?.includes(Roles.cashier)) return "Caissier";

  if (roles?.includes(Roles.staff)) return "Staff";

  return "";
}

export function getIdFromPathname(pathname: string) {
  if (pathname !== null) {
    // @ts-ignore
    const id = pathname.match(/\/(\d+)\/?/)[1];
    return id;
  }
  return null;
}

export function getInitials(fullName?: string) {
  if (!fullName) {
    return "NA";
  }

  const nameParts = fullName.trim().split(" ");
  const firstNameInitial = nameParts[0].charAt(0).toUpperCase();
  if (nameParts.length < 2) {
    return firstNameInitial;
  }
  const lastNameInitial = nameParts[nameParts.length - 1]
    .charAt(0)
    .toUpperCase();

  return firstNameInitial + lastNameInitial;
}

export function appendObjectToFormData(
  fd: FormData,
  obj: Record<string, any>
): void {
  Object.keys(obj).forEach((key) => {
    fd.append(key, obj[key]);
  });
}

export function displayFormData(fd: FormData) {
  for (const [key, value] of fd.entries()) {
    console.log(`${key}: ${value}`);
  }
}

export function formatPrice(amount: number, currency: string): string {
  return amount.toLocaleString("fr-FR", { style: "currency", currency });
}

export function formatCompactPrice(amount: number, currency: string): string {
  const actualAmount = amount / 100;

  const formatter = new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  const parts = formatter.formatToParts(0);
  const currencySymbol =
    parts.find((part) => part.type === "currency")?.value || currency;

  if (actualAmount >= 1000000) {
    return `${(actualAmount / 1000000).toFixed(1)}M ${currencySymbol}`;
  } else if (actualAmount >= 1000) {
    return `${(actualAmount / 1000).toFixed(1)}k ${currencySymbol}`;
  }

  return formatPrice(actualAmount, currency);
}

export function formatDate(date: Date, withTime = true, short = false): string {
  const options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: short ? "short" : "long",
    day: "numeric",
  };

  let formattedDate = date.toLocaleDateString("fr-FR", options);
  formattedDate = formattedDate.replace(/(\w+)/g, (word, index) =>
    index > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
  );

  if (withTime) {
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    formattedDate += ` à ${hours}h${minutes}`;
  }

  return formattedDate;
}

export function getOrderActionText(status: OrderStatus): string {
  switch (status) {
    case "accepted":
      return "Accepter";
    case "rejected":
      return "Rejeter";
    case "preparing":
      return "Préparer";
    case "ready":
      return "Prête";
    case "served":
      return "Servir";
    case "picked_up":
      return "Récupérée";
    case "delivered":
      return "Livrée";
    case "canceled":
      return "Annuler";
    default:
      return status;
  }
}

export function getOrderStatusText(status: OrderStatus, role?: Roles): string {
  switch (status) {
    case "pending":
      return "En attente";
    case "accepted":
      return role === Roles.kitchen ? "À préparer" : "Acceptée";
    case "rejected":
      return "Rejetée";
    case "preparing":
      return "En préparation";
    case "ready":
      return "Prête";
    case "served":
      return "Servie";
    case "picked_up":
      return "Récupérée";
    case "delivered":
      return "Livrée";
    case "canceled":
      return "Annulée";
    case "draft":
      return "Brouillon";
    default:
      return status;
  }
}

export const STATUS_BUTTON_STYLES: Record<string, string> = {
  accepted: "border-blue-500 bg-blue-500 text-white hover:bg-blue-600",
  preparing: "border-yellow-500 bg-yellow-500 text-white hover:bg-yellow-600",
  ready: "border-purple-500 bg-purple-500 text-white hover:bg-purple-600",
  served: "border-green-500 bg-green-500 text-white hover:bg-green-600",
  picked_up: "border-teal-500 bg-teal-500 text-white hover:bg-teal-600",
  delivered: "border-green-500 bg-green-500 text-white hover:bg-green-600",
  rejected: "border-red-500 bg-red-500 text-white hover:bg-red-600",
  canceled: "border-red-500 bg-red-500 text-white hover:bg-red-600",
};

/**
 * Orders older than this are hidden from the edit flow even when the backend
 * still allows changes — staff shouldn't reopen yesterday's tickets. Tune here.
 */
export const ORDER_EDIT_WINDOW_HOURS = 10;

/** True when `createdAt` is within the editable window (default 10h). */
export function isWithinEditWindow(
  createdAt: string | undefined,
  windowHours: number = ORDER_EDIT_WINDOW_HOURS
): boolean {
  if (!createdAt) return false;
  const created = new Date(createdAt).getTime();
  if (Number.isNaN(created)) return false;
  return Date.now() - created <= windowHours * 60 * 60 * 1000;
}

/**
 * What the customer owes: the API's totalPrice (lines + delivery fee). The
 * client-side sum is only a fallback for payloads without totalPrice.
 */
export function getOrderTotal(order: Order): number {
  if (typeof order.totalPrice === "number") return order.totalPrice;

  const itemsTotal = order.orderItems.reduce(
    (sum, item) => sum + item.price,
    0
  );
  const combosTotal =
    order.menuComboOrderItems?.reduce((sum, item) => sum + item.price, 0) ?? 0;
  const formulasTotal =
    order.menuFormulaOrderItems?.reduce((sum, item) => sum + item.price, 0) ??
    0;
  return itemsTotal + combosTotal + formulasTotal + (order.deliveryFee ?? 0);
}

/** Currency units → integer cents, rounded (`1.15 * 100` alone is 114.99…). */
export function convertPriceToCent(price: number): number {
  return Math.round(price * 100);
}
export function convertPriceFromCent(priceInCents: number): number {
  return Number(priceInCents) / 100;
}

export function displayPrice(priceInCents: number, currency: string): string {
  return formatPrice(priceInCents / 100, currency);
}

export function formatNotificationDate(dateString: string) {
  const date = parseISO(dateString);
  const now = new Date();
  const diffInDays = differenceInCalendarDays(now, date);

  if (isToday(date)) {
    return `Aujourd'hui à ${format(date, "HH:mm")}`;
  } else if (isYesterday(date)) {
    return `Hier à ${format(date, "HH:mm")}`;
  } else if (diffInDays < 7) {
    return `${format(date, "EEEE", { locale: fr })} à ${format(date, "HH:mm")}`;
  } else {
    return format(date, "d MMMM yyyy 'à' HH:mm", { locale: fr });
  }
}

export function isGranted(role: Roles[], roles?: Roles[]) {
  if (!roles) {
    return false;
  }

  return roles.some((r) => role.includes(r));
}

export function hasSign(price: string): boolean {
  if (price.startsWith("+") || price.startsWith("-")) {
    return true;
  } else {
    return false;
  }
}

// TODO: @Alpha, what is this?
export function handleError(error: QueryError) {
  console.log(
    error,
    errors.errors["/validation_errors/00000000000004690000000000000000"]
  );
}

export const isValidUploadedImage = (image?: any) => {
  return (
    image && typeof image === "string" && image.includes("/api/media_objects/")
  );
};

export function getMenuItemOptionValue(itemOption: number | MenuItemOption) {
  if (typeof itemOption === "number") {
    return itemOption;
  }
  return itemOption?.id;
}

export function getNotificationAction(notification: NotificationType) {
  switch (notification.action) {
    case "notifications.order.new_order":
      return `Une nouvelle commande a été placée`;
    case "notifications.order.status_changed":
      return `Le statut de la commande #${
        notification.metadata.order_number ?? notification.metadata.id
      } a été changé de ${getOrderStatusText(
        notification.metadata.old_status as OrderStatus
      )} à ${getOrderStatusText(
        notification.metadata.new_status as OrderStatus
      )}`;
    case "notifications.order.customer_update":
      return `La commande #${
        notification.metadata.order_number ?? notification.metadata.id
      } a été modifiée par l'utilisateur`;
    case "notifications.inventory.low_stock":
      return `Stock faible : ${notification.metadata.ingredient_name} (${notification.metadata.available} ≤ ${notification.metadata.threshold})`;
    case "notifications.inventory.availability_shortfall":
      return `Commande ${notification.metadata.order_number} : ${notification.metadata.shortfall_count} ingrédient(s) insuffisant(s)`;
    case "notifications.inventory.reservation_failed":
    case "notifications.inventory.commit_failed":
    case "notifications.inventory.release_failed":
    case "notifications.inventory.restore_failed":
      return `Erreur inventaire sur ${notification.metadata.order_number ?? ""} : ${notification.metadata.error ?? "inconnue"}`;
    default:
      return "Nouvelle notification";
  }
}

export const INVENTORY_NOTIFICATION_ACTIONS = [
  "notifications.inventory.low_stock",
  "notifications.inventory.availability_shortfall",
  "notifications.inventory.reservation_failed",
  "notifications.inventory.commit_failed",
  "notifications.inventory.release_failed",
  "notifications.inventory.restore_failed",
] as const;

export function getMenuItemDisplayPrice(menuItem: MenuItem): number {
  // Case 0: the price the API resolved for this placement — the per-menu
  // override, else the dish's base price. Prefer it over basePrice, which
  // ignores both and is wrong for a dish shared across menus (SFAPI-95).
  if (menuItem.price > 0) {
    return menuItem.price;
  }

  // Case 1: Use basePrice if greater than 0
  if (menuItem.basePrice > 0) {
    return menuItem.basePrice;
  }

  // Case 2: Find a template option and get its smallest price
  const templateOption = menuItem.itemOptions?.find(
    (option) => option.menuItemOption.isTemplate === true
  );

  if (
    templateOption &&
    templateOption.menuItemOption.menuItemOptionValues?.length > 0
  ) {
    // Find the minimum base price among all values of the template option
    const prices = templateOption.menuItemOption.menuItemOptionValues.map(
      (value) => value.basePrice ?? 0
    );
    const minPrice = Math.min(...prices);

    if (minPrice > 0) {
      return minPrice;
    }
  }

  // Case 3: Get the smallest price of the first menu item option
  if (menuItem.itemOptions?.length > 0) {
    const firstOption = menuItem.itemOptions[0];

    // Check if this option has itemOptionPrices
    if (firstOption.itemOptionPrices?.length > 0) {
      // Get the minimum price modifier (could be negative)
      const priceModifiers = firstOption.itemOptionPrices.map(
        (price) => price.priceModifier || 0
      );
      const minPriceModifier = Math.min(...priceModifiers);
      return Math.max(0, minPriceModifier); // Ensure it's not negative
    }

    // If no itemOptionPrices, try to get values from the option
    if (firstOption.menuItemOption.menuItemOptionValues?.length > 0) {
      const prices = firstOption.menuItemOption.menuItemOptionValues.map(
        (value) => value.basePrice ?? 0
      );
      const minPrice = Math.min(...prices);

      if (minPrice > 0) {
        return minPrice;
      }
    }
  }

  // Default: display 0
  return 0;
}

export function formatSideType(type: string): string {
  switch (type) {
    case AllowedSideType.FRIES:
      return "Frites";
    case AllowedSideType.SALADS:
      return "Salades";
    default:
      return type;
  }
}

export function formatPhoneNumber(
  phone: string,
  defaultCountry: CountryCode = "SN"
): string {
  try {
    // Try to parse the phone number
    const phoneNumber = parsePhoneNumberWithError(phone, defaultCountry);

    if (!phoneNumber.isValid()) {
      // If parsing fails or invalid, return original
      return phone;
    }

    // Format with international format and spaces
    return phoneNumber.formatInternational();
  } catch {
    // If any error occurs, return the original phone number
    return phone;
  }
}

export function formatRelativeDate(date: Date): string {
  const now = new Date();
  const diffInDays = differenceInCalendarDays(now, date);

  if (diffInDays === 0) {
    return "Aujourd'hui";
  } else if (diffInDays === 1) {
    return "Hier";
  } else if (diffInDays < 7) {
    return `Il y a ${diffInDays} jours`;
  } else if (diffInDays < 30) {
    const weeks = Math.floor(diffInDays / 7);
    return `Il y a ${weeks} semaine${weeks > 1 ? "s" : ""}`;
  } else if (diffInDays < 365) {
    const months = Math.floor(diffInDays / 30);
    return `Il y a ${months} mois`;
  } else {
    const years = Math.floor(diffInDays / 365);
    return `Il y a ${years} an${years > 1 ? "s" : ""}`;
  }
}

export const DAYS_OF_WEEK = [
  { value: 0, label: "Dimanche", abbr: "Dim" },
  { value: 1, label: "Lundi", abbr: "Lun" },
  { value: 2, label: "Mardi", abbr: "Mar" },
  { value: 3, label: "Mercredi", abbr: "Mer" },
  { value: 4, label: "Jeudi", abbr: "Jeu" },
  { value: 5, label: "Vendredi", abbr: "Ven" },
  { value: 6, label: "Samedi", abbr: "Sam" },
];

export const getDayLabel = (dayValue: number): string => {
  const day = DAYS_OF_WEEK.find((d) => d.value === dayValue);
  return day?.label ?? "";
};

export const getDayAbbr = (dayValue: number): string => {
  const day = DAYS_OF_WEEK.find((d) => d.value === dayValue);
  return day?.abbr ?? "";
};

// POS utility functions

export function stripTrailingZeros(value: string): string {
  if (!value.includes(".")) return value;
  return value.replace(/\.?0+$/, "");
}

export function formatStock(quantity: string, unit: string): string {
  return `${stripTrailingZeros(quantity)} ${unit}`;
}

export function getPaymentMethodLabel(method: PaymentMethodName): string {
  switch (method) {
    case "cash":
      return "Espèces";
    case "card":
      return "Carte";
    case "mobile money":
      return "Mobile Money";
    case "wave":
      return "Wave";
    case "orange money":
      return "Orange Money";
    default:
      return method;
  }
}

export function getSessionStatusLabel(status: SessionStatus): string {
  switch (status) {
    case "open":
      return "Ouverte";
    case "closed":
      return "Fermée";
    default:
      return status;
  }
}

export function getStockAdjustmentReasonLabel(
  reason: StockAdjustmentReason
): string {
  switch (reason) {
    case "delivery":
      return "Livraison";
    case "waste":
      return "Perte";
    case "correction":
      return "Correction";
    case "return":
      return "Retour";
    case "order_deduction":
      return "Déduction commande";
    case "cost_correction":
      return "Correction de coût";
    case "cancellation":
      return "Annulation";
    default:
      return reason;
  }
}

export function getOrderSourceLabel(source: OrderSource): string {
  switch (source) {
    case "pos":
      return "Caisse";
    case "qr_code":
    case "qrcode":
      return "QR Code";
    case "api":
      return "API";
    case "phone":
      return "Téléphone";
    case "web":
      return "Web";
    default:
      return source;
  }
}

export function getOrderTypeLabel(type: OrderType): string {
  switch (type) {
    case "dine_in":
      return "Sur place";
    case "takeaway":
      return "À emporter";
    case "delivery":
      return "Livraison";
    default:
      return type;
  }
}

export function getItemStatusLabel(status: OrderItemStatus): string {
  switch (status) {
    case "pending":
      return "En attente";
    case "preparing":
      return "En préparation";
    case "ready":
      return "Prêt";
    default:
      return status;
  }
}

export function formatVariance(variance: number, currency: string): string {
  const sign = variance >= 0 ? "+" : "";
  return `${sign}${formatPrice(variance, currency)}`;
}

/**
 * Normalizes a user-typed decimal string to a Postgres-safe dot decimal (e.g. "1,75" -> "1.75").
 *
 * EU/French keypads emit a comma as the decimal separator; sending it verbatim makes the API
 * reject the value (Postgres 22P02). A lone comma is treated as the decimal separator; input
 * that is empty, ambiguous (multiple/mixed separators), or not a plain decimal returns null so
 * the caller can reject it instead of sending garbage.
 */
export function normalizeDecimalInput(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  // Ambiguous grouping (e.g. "1,000.50", "1.000,50", "1,2,3") — refuse to guess.
  if (
    (trimmed.match(/,/g)?.length ?? 0) > 1 ||
    (trimmed.includes(",") && trimmed.includes("."))
  ) {
    return null;
  }

  const dotted = trimmed.replace(",", ".");
  // Plain decimal only: optional sign, digits with an optional fractional part (or ".5").
  if (!/^-?(\d+(\.\d+)?|\.\d+)$/.test(dotted)) return null;

  return dotted;
}

/**
 * Parses a user-typed money amount (currency units, "1,75" or "1.75") into
 * integer cents. Returns `null` when the input is empty or cannot be read
 * unambiguously (see `normalizeDecimalInput`), so callers show their own
 * validation message instead of sending NaN or a truncated amount. The sign
 * is kept: callers decide whether negatives or zero are allowed.
 */
export function parseMoneyToCents(
  input: string | null | undefined
): number | null {
  const normalized = normalizeDecimalInput(input ?? "");
  if (normalized === null) return null;
  // Shift the decimal point in the string itself: "1.005e2" parses to exactly
  // 100.5, whereas 1.005 * 100 is 100.49999999999999.
  const cents = Math.round(Number(`${normalized}e2`));
  return cents === 0 ? 0 : cents;
}
